import { useState, useCallback } from 'react';

/**
 * Options for useDraggableList hook
 *
 * @property isDisabled - Whether drag functionality is disabled
 */
export interface IUseDraggableListOptions {
	isDisabled?: boolean;
}

/**
 * Return value from useDraggableList hook
 *
 * @property draggedIndex - Index of the item currently being dragged (null if not dragging)
 * @property dragOverIndex - Index of the item currently being dragged over (null if not dragging over)
 * @property handleDragStart - Handler for when drag starts
 * @property handleDragEnd - Handler for when drag ends
 * @property handleDragOver - Handler for when dragging over an item
 * @property handleDragLeave - Handler for when drag leaves an item
 * @property handleDrop - Handler for when item is dropped
 * @property handleMoveUp - Handler for moving item up in the list
 * @property handleMoveDown - Handler for moving item down in the list
 * @property handleDelete - Handler for deleting an item from the list
 */
export interface IUseDraggableListReturn {
	draggedIndex: number | null;
	dragOverIndex: number | null;
	handleDragStart: (index: number) => void;
	handleDragEnd: () => void;
	handleDragOver: (e: React.DragEvent, index: number) => void;
	handleDragLeave: () => void;
	handleDrop: (index: number) => void;
	handleMoveUp: (index: number) => void;
	handleMoveDown: (index: number) => void;
	handleDelete: (index: number) => void;
}

/**
 * Hook for managing drag-and-drop reordering of lists
 *
 * @param items - Array of items to manage
 * @param onReorder - Callback when items are reordered
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * const [items, setItems] = useState([...]);
 * const { draggedIndex, dragOverIndex, handleDragStart, handleDragEnd, ... } = useDraggableList(
 *   items,
 *   setItems
 * );
 *
 * return items.map((item, index) => (
 *   <DraggableListItem
 *     isDragging={draggedIndex === index}
 *     isDragOver={dragOverIndex === index}
 *     onDragStart={() => handleDragStart(index)}
 *     onDragEnd={handleDragEnd}
 *     onDragOver={(e) => handleDragOver(e, index)}
 *     onDragLeave={handleDragLeave}
 *     onDrop={() => handleDrop(index)}
 *     onMoveUp={() => handleMoveUp(index)}
 *     onMoveDown={() => handleMoveDown(index)}
 *     onDelete={() => handleDelete(index)}
 *   >
 *     {item.label}
 *   </DraggableListItem>
 * ));
 * ```
 */
export const useDraggableList = <T,>(
	items: T[],
	onReorder: (items: T[]) => void,
	options: IUseDraggableListOptions = {}
): IUseDraggableListReturn => {
	const { isDisabled = false } = options;

	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

	const handleDragStart = useCallback(
		(index: number) => {
			if (isDisabled) return;
			setDraggedIndex(index);
		},
		[isDisabled]
	);

	const handleDragEnd = useCallback(() => {
		setDraggedIndex(null);
		setDragOverIndex(null);
	}, []);

	const handleDragOver = useCallback(
		(e: React.DragEvent, index: number) => {
			e.preventDefault();
			if (draggedIndex === null || isDisabled) return;
			setDragOverIndex(index);
		},
		[draggedIndex, isDisabled]
	);

	const handleDragLeave = useCallback(() => {
		setDragOverIndex(null);
	}, []);

	const handleDrop = useCallback(
		(index: number) => {
			if (draggedIndex === null || isDisabled || draggedIndex === index) {
				setDraggedIndex(null);
				setDragOverIndex(null);
				return;
			}

			const newItems = [...items];
			const [removed] = newItems.splice(draggedIndex, 1);
			newItems.splice(index, 0, removed);

			onReorder(newItems);
			setDraggedIndex(null);
			setDragOverIndex(null);
		},
		[draggedIndex, isDisabled, items, onReorder]
	);

	const handleMoveUp = useCallback(
		(index: number) => {
			if (index === 0 || isDisabled) return;
			const newItems = [...items];
			[newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
			onReorder(newItems);
		},
		[items, onReorder, isDisabled]
	);

	const handleMoveDown = useCallback(
		(index: number) => {
			if (index === items.length - 1 || isDisabled) return;
			const newItems = [...items];
			[newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
			onReorder(newItems);
		},
		[items, onReorder, isDisabled]
	);

	const handleDelete = useCallback(
		(index: number) => {
			if (isDisabled) return;
			const newItems = items.filter((_, i) => i !== index);
			onReorder(newItems);
		},
		[items, onReorder, isDisabled]
	);

	return {
		draggedIndex,
		dragOverIndex,
		handleDragStart,
		handleDragEnd,
		handleDragOver,
		handleDragLeave,
		handleDrop,
		handleMoveUp,
		handleMoveDown,
		handleDelete,
	};
};

